import { ReactNode } from 'react';

import { IcClose } from '@quizzach/assets';
import { ModalCloseBtn, ShellPanel } from '@quizzach/styles';
import { useTranslation } from 'react-i18next';
import { Modal } from './Modal';

interface ModalShellProps {
	/** Controlled visibility; exit animation plays when it flips to false. */
	show?: boolean;
	onClose?: () => void;
	/** 13px caps micro-label above the title. */
	label?: string;
	/** Fjalla uppercase title. */
	title?: string;
	/** Icon slot rendered above the title. */
	icon?: ReactNode;
	/** Action row: primary/secondary buttons (BtnTeal / BtnLine). */
	actions?: ReactNode;
	size?: 'sm' | 'md' | 'lg';
	center?: boolean;
	children?: ReactNode;
}

/**
 * The one shape every dialog shares: design-system panel (bottom sheet on
 * touch layouts via the core Modal), round close button, label/title/icon
 * slots, scrollable body and an action row.
 */
export const ModalShell = ({
	show = true,
	onClose,
	label,
	title,
	icon,
	actions,
	size = 'md',
	center,
	children,
}: ModalShellProps) => {
	const { t } = useTranslation();

	return (
		<Modal show={show} closeModal={onClose} explanation={size === 'lg'}>
			<ShellPanel $center={center} $size={size}>
				<span className='m-handle' aria-hidden='true' />
				{onClose && (
					<ModalCloseBtn
						type='button'
						aria-label={t('common.close')}
						onClick={onClose}
					>
						<IcClose size={15} />
					</ModalCloseBtn>
				)}
				{icon && <div className='m-icon'>{icon}</div>}
				{label && <span className='m-label'>{label}</span>}
				{title && <h3 className='m-title'>{title}</h3>}
				<div className='m-body'>{children}</div>
				{actions && <div className='m-actions'>{actions}</div>}
			</ShellPanel>
		</Modal>
	);
};
